import { useState, useEffect } from 'react';
import { DollarSign, Save, Loader2, Search, Star, Percent } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useUser, useAuthStore } from '../../store/useAuthStore';
import { toast } from '../../lib/toast';

interface CatalogProduct {
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  price: number;
  directToPatient?: boolean;
}

export function StorePricing() {
  const user = useUser();
  const { getCurrentUser } = useAuthStore();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [markups, setMarkups] = useState<Record<string, number>>({});
  const [originalMarkups, setOriginalMarkups] = useState<Record<string, number>>({});
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
  const [originalHiddenProducts, setOriginalHiddenProducts] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [bulkPercent, setBulkPercent] = useState('');
  const [roundingIncrement, setRoundingIncrement] = useState(1);
  const [savingFeatured, setSavingFeatured] = useState(false);

  const featuredId = user?.physicianData?.featuredStoreProductId || '';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [productsRes, markupsRes] = await Promise.all([
        apiClient.getMyProducts(),
        apiClient.getPatientPayMarkupDefaults()
      ]);

      if (productsRes.success && productsRes.data) {
        // Filter to DTP-eligible products only
        const dtpProducts = (productsRes.data.products || []).filter(
          (p: any) => p.directToPatient !== false
        );
        setProducts(dtpProducts);
      }

      if (markupsRes.success && markupsRes.data) {
        setMarkups(markupsRes.data.defaults || {});
        setOriginalMarkups(markupsRes.data.defaults || {});
        const hidden = new Set(markupsRes.data.hiddenProducts || []);
        setHiddenProducts(hidden);
        setOriginalHiddenProducts(new Set(hidden));
      }
    } catch {
      setError('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkupChange = (productId: string, value: string) => {
    const num = value === '' ? 0 : parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setMarkups(prev => ({ ...prev, [productId]: num }));
    setSuccess('');
  };

  const toggleProductVisibility = (productId: string) => {
    setHiddenProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
    setSuccess('');
  };

  const applyBulkMarkup = () => {
    const pct = parseFloat(bulkPercent);
    if (isNaN(pct) || pct < 0) return;
    const updated: Record<string, number> = { ...markups };
    for (const product of products) {
      const cost = product.price || 0;
      const raw = cost * (pct / 100);
      const rounded = roundingIncrement > 0
        ? Math.ceil(raw / roundingIncrement) * roundingIncrement
        : Math.round(raw * 100) / 100;
      updated[product._id] = Math.max(rounded, 0);
    }
    setMarkups(updated);
    setSuccess('');
  };

  const setFeatured = async (productId: string | null) => {
    setSavingFeatured(true);
    const res = await apiClient.updateFeaturedStoreProduct(productId);
    setSavingFeatured(false);
    if (res.success) {
      await getCurrentUser();
      toast.success(productId ? 'Featured product set' : 'Featured product cleared');
    } else {
      toast.error(res.error || 'Failed to update featured product');
    }
  };

  const hasChanges =
    JSON.stringify(markups) !== JSON.stringify(originalMarkups) ||
    JSON.stringify([...hiddenProducts].sort()) !== JSON.stringify([...originalHiddenProducts].sort());

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Only save markups for products that have a non-zero value or were previously set
      const toSave: Record<string, number> = {};
      for (const p of products) {
        const val = markups[p._id] ?? 0;
        if (val > 0 || originalMarkups[p._id] !== undefined) {
          toSave[p._id] = val;
        }
      }
      const res = await apiClient.updatePatientPayMarkupDefaults(toSave, [...hiddenProducts]);
      if (res.success) {
        setOriginalMarkups({ ...markups });
        setOriginalHiddenProducts(new Set(hiddenProducts));
        setSuccess('Store pricing saved');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.error || 'Failed to save');
      }
    } catch {
      setError('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const filtered = search
    ? products.filter(p =>
        p.displayName.toLowerCase().includes(search.toLowerCase()) ||
        p.drugName.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <DollarSign className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="font-medium text-gray-700 mb-1">No store products</p>
        <p className="text-sm">No products in your catalog are enabled for direct-to-patient ordering. Contact your administrator to enable products for your store.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">
            Set your markup on each product and toggle which products appear in your store. Patients will see the <strong>store price</strong> (your cost + markup).
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Pricing
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Bulk markup helper */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Percent className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Bulk markup helper</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0"
              step="1"
              value={bulkPercent}
              onChange={e => setBulkPercent(e.target.value)}
              placeholder="e.g. 25"
              className="w-24 px-3 py-1.5 border border-blue-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-blue-700">%</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-blue-600">Round to $</span>
            <select
              value={roundingIncrement}
              onChange={e => setRoundingIncrement(Number(e.target.value))}
              className="px-2 py-1.5 border border-blue-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>$1</option>
              <option value={5}>$5</option>
              <option value={10}>$10</option>
              <option value={0}>No rounding</option>
            </select>
          </div>
          <button
            onClick={applyBulkMarkup}
            disabled={!bulkPercent}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply to all
          </button>
        </div>
      </div>

      {products.length > 6 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-center px-3 py-3 font-medium text-gray-700 w-16">Show</th>
              <th className="text-center px-2 py-3 font-medium text-gray-700 w-12" title="Star to feature in storefront hero">★</th>
              <th className="text-left px-4 py-3 font-medium text-gray-700">Product</th>
              <th className="text-right px-4 py-3 font-medium text-gray-700 w-28">Your Cost</th>
              <th className="text-right px-4 py-3 font-medium text-gray-700 w-36">Markup ($)</th>
              <th className="text-right px-4 py-3 font-medium text-gray-700 w-32">Store Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(product => {
              const markup = markups[product._id] ?? 0;
              const storePrice = product.price + markup;
              const isHidden = hiddenProducts.has(product._id);
              const isFeatured = featuredId === product._id;
              return (
                <tr key={product._id} className={`hover:bg-gray-50 ${isHidden ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-3 text-center">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!isHidden}
                      onClick={() => toggleProductVisibility(product._id)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        !isHidden ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          !isHidden ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <button
                      onClick={() => setFeatured(isFeatured ? null : product._id)}
                      disabled={savingFeatured || isHidden}
                      className={`p-1 rounded transition-colors ${
                        isFeatured ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 hover:text-amber-500'
                      } ${(savingFeatured || isHidden) ? 'cursor-not-allowed opacity-60' : ''}`}
                      title={isHidden ? 'Show this product first' : isFeatured ? 'Featured — click to unstar' : 'Star to feature in storefront'}
                    >
                      <Star className={`w-4 h-4 ${isFeatured ? 'fill-current' : ''}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{product.displayName}</p>
                    <p className="text-xs text-gray-500">{product.format} &middot; {product.size}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600 tabular-nums">
                    ${product.price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-gray-400">+$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={markup || ''}
                        onChange={e => handleMarkupChange(product._id, e.target.value)}
                        placeholder="0.00"
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded text-right text-sm tabular-nums focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">
                    ${storePrice.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
